import React, { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import { v4 as uuid4 } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { VariableResultColumnData, VariableResultSingleCell, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { SingleCell16 } from '@makyoapp/makyo-icon/icon'
import { SimpleInput } from '@makyoapp/makyo-ui'

import { Help } from '@/components/Help'

import { VariableColumnDataBody } from './VariableColumnData'
import { VariableWrapper } from './VariableWrapper'

export default function VariableSingleCell({
  id,
  onChange,
  columnTypes = Object.values(ColumnTypes),
  excludeColIds,
}: {
  id?: string
  onChange?: (newValue: VariableResultSingleCell) => void
  columnTypes?: ColumnTypes[]
  excludeColIds?: string[]
}) {
  const [show, setShow] = useBoolean(false)
  const [us, setUs] = useBoolean(false)
  const [rowNum, setRowNum] = useState('1')
  return (
    <VariableWrapper
      id={id}
      show={show}
      setShow={setShow}
      title={<Trans>Single Cell Value</Trans>}
      icon={<SingleCell16 width={16} height={16} />}
      defaultHeight={false}
    >
      <VariableSingleCellBody
        value={{
          id: uuid4(),
          flag: VariableSelectFeatureFlag.SingleCell,
          tbl: undefined,
          col: undefined,
          row: '',
          userSpecific: false,
        }}
        setShow={setShow}
        userSpecific={us}
        setUserSpecific={setUs}
        rowNum={rowNum}
        setRowNum={setRowNum}
        columnTypes={columnTypes}
        onChange={onChange}
        isSingleCell={true}
        excludeColIds={excludeColIds}
      />
    </VariableWrapper>
  )
}

export function VariableSingleCellBody({
  onChange,
  columnTypes = Object.values(ColumnTypes),
  setShow,
  value,
  userSpecific,
  setUserSpecific,
  rowNum,
  setRowNum,
  isSingleCell,
  excludeColIds,
}: {
  onChange?: (newValue: VariableResultSingleCell) => void
  columnTypes?: ColumnTypes[]
  setShow: (nextShow: boolean) => void
  value: VariableResultSingleCell
  userSpecific: boolean
  setUserSpecific: (next: boolean) => void
  rowNum: string
  setRowNum: (next: string) => void
  isSingleCell: boolean
  excludeColIds?: string[]
}) {
  const { t } = useTranslation()

  const handleChange = useCallback(
    (v: VariableResultColumnData) => {
      const val: VariableResultSingleCell = {
        ...v,
        flag: VariableSelectFeatureFlag.SingleCell,
        row: rowNum,
      }
      onChange?.(val)
    },
    [rowNum, onChange]
  )
  return (
    <VariableColumnDataBody
      setShow={setShow}
      userSpecific={userSpecific}
      setUserSpecific={setUserSpecific}
      columnTypes={columnTypes}
      value={value as unknown as VariableResultColumnData}
      onChange={handleChange}
      isSingleCell={isSingleCell}
      excludeColIds={excludeColIds}
    >
      <div className='flex-c px-2 w-full'>
        <SimpleInput
          type='text'
          inputClassName='min-w-0'
          value={rowNum}
          placeholder={t('Enter Row Number')}
          onChange={(e) => {
            const val = e.currentTarget.value
            if (val === '-' || val === '') {
              setRowNum(val)
            } else {
              const numVal = Number(val)
              if (!isNaN(numVal)) {
                setRowNum(numVal.toString())
              }
            }
          }}
        />
        <Help>
          <Trans>
            <strong>Row Number:</strong> Use negative number to start from last row. eq: 1 equals first row, -1 equals
            last row.
          </Trans>
        </Help>
      </div>
    </VariableColumnDataBody>
  )
}
